// Offline System → Sub-system taxonomy reader against the PowerSync
// local SQLite mirror.
//
// When the PowerSync capture adapter is active
// (VITE_USE_POWERSYNC_CAPTURE_CLIENT='true') the "Add asset" dialog's
// SubSystemPicker (the "Pick a category" step of the field-added
// variant flow) routes here instead of GET /asset-systems and
// GET /asset-systems/:id/sub-systems, which are unreachable offline.
//
// Faithful mirror of apps/backend/src/classification/list-systems.ts
// and list-sub-systems.ts so the picker offers the same categories
// online and offline — field-added asset types are part of the
// contractual baseline, so a divergent category list could file a
// variant under the wrong taxonomy slot.
//
// Parity notes:
//   • Ordering — plain `display_order ASC, name ASC`, matching the
//     backend's Drizzle `asc(name)` (Postgres default collation, byte
//     order) and SQLite's default BINARY collation. We deliberately do
//     NOT use `COLLATE NOCASE` (that is a search-only adaptation in
//     search-asset-types-local.ts for ILIKE ordering) and add no `id`
//     tie-break: `name` is uniquely indexed per parent, matching the
//     backend, which has no id tie-break either.
//   • Scope — systems are filtered to the single active contract via
//     the shared resolveActiveContractId; sub-systems are scoped by
//     their `system_id` alone, exactly as the backend's sub-system
//     SELECT does (the backend's contract check lives in the parent
//     404 guard, which the picker can never trip — see below).
//   • No-contract asymmetry — the backend returns an empty list for
//     systems but a 404 for sub-systems when no active contract
//     exists. We drop the 404: the picker only ever calls
//     loadSubSystems with ids returned by loadSystems, so a missing /
//     rejected parent (and therefore the no-contract case) is
//     unreachable here. With no contract, loadSystems returns [] and
//     loadSubSystems is never called.

import {
  resolveActiveContractId,
  type LocalSearchDb,
} from './search-asset-types-local'
import {
  type AssetSubSystemListRow,
  type AssetSystemListRow,
} from '../use-classification'

import { db } from './client'

const LOAD_SYSTEMS_SQL = `
  SELECT
    id AS id,
    name AS name,
    code AS code,
    display_order AS displayOrder
  FROM asset_systems
  WHERE contract_id = ?
    AND rejected_at IS NULL
  ORDER BY display_order ASC, name ASC
`

const LOAD_SUB_SYSTEMS_SQL = `
  SELECT
    id AS id,
    system_id AS systemId,
    name AS name,
    code AS code,
    display_order AS displayOrder
  FROM asset_sub_systems
  WHERE system_id = ?
    AND rejected_at IS NULL
  ORDER BY display_order ASC, name ASC
`

// Raw rows off the SQLite mirror. The snake→camel aliasing in the SQL
// lands these in the DTO shape already; the explicit map below guards
// against shape drift if the SELECT ever changes.
interface LocalSystemRow {
  id: string
  name: string
  code: string | null
  displayOrder: number
}

interface LocalSubSystemRow {
  id: string
  systemId: string
  name: string
  code: string | null
  displayOrder: number
}

function toSystemRow(row: LocalSystemRow): AssetSystemListRow {
  return {
    id: row.id,
    name: row.name,
    code: row.code,
    displayOrder: row.displayOrder,
  }
}

function toSubSystemRow(row: LocalSubSystemRow): AssetSubSystemListRow {
  return {
    id: row.id,
    systemId: row.systemId,
    name: row.name,
    code: row.code,
    displayOrder: row.displayOrder,
  }
}

// Testable cores — take the db so unit tests can pass a fake.

export async function loadSystemsLocalWithDb(
  database: LocalSearchDb,
): Promise<AssetSystemListRow[]> {
  const activeContractId = await resolveActiveContractId(database)
  if (activeContractId === null) return []
  const rows = await database.getAll<LocalSystemRow>(LOAD_SYSTEMS_SQL, [
    activeContractId,
  ])
  return rows.map(toSystemRow)
}

export async function loadSubSystemsLocalWithDb(
  database: LocalSearchDb,
  systemId: string,
): Promise<AssetSubSystemListRow[]> {
  const rows = await database.getAll<LocalSubSystemRow>(LOAD_SUB_SYSTEMS_SQL, [
    systemId,
  ])
  return rows.map(toSubSystemRow)
}

// Production wrappers matching the SubSystemPicker loader prop shapes.
// A superseded picker mount aborts its signal; we throw the same
// AbortError shape fetch does so the picker's isAbortError swallow
// discards the stale result instead of overwriting a newer render.

export async function loadSystemsLocal(init: {
  signal: AbortSignal
}): Promise<AssetSystemListRow[]> {
  const systems = await loadSystemsLocalWithDb(db)
  if (init.signal.aborted) {
    throw new DOMException('Aborted', 'AbortError')
  }
  return systems
}

export async function loadSubSystemsLocal(
  systemId: string,
  init: { signal: AbortSignal },
): Promise<AssetSubSystemListRow[]> {
  const subSystems = await loadSubSystemsLocalWithDb(db, systemId)
  if (init.signal.aborted) {
    throw new DOMException('Aborted', 'AbortError')
  }
  return subSystems
}
